import type React from 'react'
import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stack,
  Link,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Input,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  StackDivider,
  Center,
  Grid,
  GridItem,
  useClipboard,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroup,
  MenuOptionGroup,
  MenuItemOption,
} from '@chakra-ui/react'
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CellProps, Column } from 'react-table'
import { useTable } from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import styled from 'styled-components'
import { Base64 } from 'js-base64'
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import { CopyIcon } from '@chakra-ui/icons'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import type {
  MediaAuditLog,
  MediaAuditLogsRequest,
  MediaAuditLogsResponse,
  AuditLogsErrorResponse,
  ListAllUsersInLogsResponse,
  ListAllActiveUsersResponse,
} from '../../interfaces'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'
import { DateSelector } from '../../components/Analytics'
import SearchBar from '../../components/SearchBar'
import { MediaLibOperationEnum } from '../../../utils/operations'
import { ReactSyntaxHighlighter } from '../../components/ReactSyntaxHighlighter'
import MultiSelectMenu from '../../components/AuditLogs/MultiSelectMenu'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'
import Hover from '../../components/Hover'

const codeBlockStyles = {
  backgroundColor: '#193055',
  padding: 30,
  lineHeight: '20px',
  fontSize: 14,
  marginRight: '40px',
}

const ReactSyntaxHighlighterWrapper = styled(ReactSyntaxHighlighter)`
  .react-syntax-highlighter-line-number {
    color: #888 !important;
    padding-left: 0.5em !important;
    padding-right: 1.5em !important;
    min-width: 2.25em !important;
  }
  pre {
    margin: 0px !important;
  }
  code {
    span {
      font-family: monospace, monospace !important;
    }
  }
`

const MediaAuditLogsLayout = (): React.ReactElement => {
  const { user } = useContext(UserContext)
  const featureEnabled =
    user?.clientNumber &&
    user.pricingPlan.featuresList.includes(FeaturesEnum.CENTRAL_AUDIT_LOGS)
  return (
    <BaseLayout>
      <Header />
      <MainContentWrapper style={{ gap: '0.5em' }}>
        {featureEnabled ? (
          <MainContent clientNumber={user.clientNumber} />
        ) : (
          <CustomPlanCTA feature={FeaturesEnum.CENTRAL_AUDIT_LOGS} />
        )}
      </MainContentWrapper>
    </BaseLayout>
  )
}

export default MediaAuditLogsLayout

type Nullable<T> = T | null

const MediaLibOperationsValueToLabel: { [key: string]: string } = Object.values(
  MediaLibOperationEnum,
).reduce((acc, curr) => {
  acc[curr.value] = curr.label
  return acc
}, {})

function MainContent({ clientNumber }): React.ReactElement {
  const initialStartDate = moment().subtract(1, 'month').startOf('day').toDate()
  const initialEndDate = moment().endOf('day').toDate()

  const { t } = useTranslation()
  const toast = useToast()

  const [logs, setLogs] = useState<MediaAuditLog[]>([])
  const [cursor, setCursor] = useState<Nullable<[number, string]>>(null)
  const [users, setUsers] = useState<ListAllUsersInLogsResponse[]>()
  const [path, setPath] = useState<string>('')
  const [requestId, setRequestId] = useState<string>('')
  const [disableApply, setDisableApply] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate)
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate)
  const [userList, setUserList] = useState<string[]>([])
  const [operations, setOperations] = useState<string[]>([])
  const [logDisplayIndex, setLogDisplayIndex] = useState<number>(0)

  const { request: requestLogs, loading: loadingLogs = false } =
    useFetch<MediaAuditLogsRequest>({
      cachePolicy: CachePolicies.NO_CACHE,
    })
  const { request: requestUsers } = useFetch<ListAllActiveUsersResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getLogs = async (ignoreCursor): Promise<void> => {
    setIsRefreshing(true)
    setDisableApply(true)
    const limit = 10
    const response = (await requestLogs.post(
      `/api/clients/${clientNumber}/media-audit-logs/search`,
      {
        ...(userList.length ? { userEmails: userList } : {}),
        ...(operations.length ? { operations } : {}),
        ...(path ? { path } : {}),
        ...(requestId ? { requestId } : {}),
        ...(startDate || endDate
          ? {
              timeInterval: {
                ...(startDate ? { start: startDate } : {}),
                ...(endDate ? { end: endDate } : {}),
              },
            }
          : {}),
        limit,
        cursor: ignoreCursor ? null : cursor,
      },
    )) as unknown as MediaAuditLogsResponse | AuditLogsErrorResponse

    if (requestLogs.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      const { logs: newLogs, paging } = response as MediaAuditLogsResponse
      if (ignoreCursor) {
        setLogs(newLogs)
      } else {
        setLogs(logs => logs.concat(newLogs))
      }
      setCursor(newLogs.length < limit ? null : paging.cursor)
    }
    setIsRefreshing(false)
    setDisableApply(false)
  }

  const getUsers = async (): Promise<void> => {
    const currentUsersList: ListAllActiveUsersResponse[] =
      await requestUsers.get(
        `/api/clients/${clientNumber}/media-audit-logs/all-active-users`,
      )
    if (requestUsers.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    setUsers(currentUsersList)
  }

  useEffect(() => {
    void getUsers()
    void getLogs(true)
  }, [])

  const handleSubmit = useCallback(() => {
    void getLogs(true)
  }, [startDate, endDate, userList, operations, path, requestId])

  const loadMore = () => {
    if (cursor) {
      void getLogs(false)
    }
  }

  const columns = useMemo<Array<Column<MediaAuditLog>>>(
    () => getColumns({ t, users }),
    [users],
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<MediaAuditLog>({
      columns,
      data: logs,
      initialState: {
        pageIndex: 0,
        sortBy: [
          {
            id: 'Time',
            desc: true,
          },
        ],
      },
      pageCount: -1,
    })

  const rowItems = useMemo(
    () =>
      rows.length
        ? rows.map(row => {
            prepareRow(row)
            const { key, ...restRowProps } = row.getRowProps()
            restRowProps.style = {
              background: row.index === logDisplayIndex ? '#F7FAFC' : 'white',
            }
            return (
              <Tr
                key={key}
                {...restRowProps}
                onClick={() => void setLogDisplayIndex(row.index)}
              >
                {row.cells.map(cell => {
                  const { key: keyRow, ...restCellProps } = cell.getCellProps()
                  return (
                    <Td key={keyRow} {...restCellProps}>
                      <ChakraText>{cell.render('Cell')}</ChakraText>
                    </Td>
                  )
                })}
              </Tr>
            )
          })
        : [],
    [rows, logDisplayIndex],
  )

  return (
    <>
      <FilterBar
        users={users}
        startDate={startDate}
        endDate={endDate}
        path={path}
        requestId={requestId}
        disableApply={disableApply}
        isRefreshing={isRefreshing}
        setUserList={setUserList}
        setOperations={setOperations}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setPath={setPath}
        setRequestId={setRequestId}
        setDisableApply={setDisableApply}
        handleSubmit={handleSubmit}
      />
      <Divider mt={5} />
      <HStack
        overflow="hidden"
        alignItems="flex-start"
        divider={<StackDivider borderColor="gray.200" alignSelf="stretch" />}
      >
        <VStack
          alignItems="flex-start"
          paddingRight={4}
          paddingTop={4}
          w="100%"
        >
          <LogsTable
            rowItems={rowItems}
            getTableProps={getTableProps}
            headerGroups={headerGroups}
            getTableBodyProps={getTableBodyProps}
            loadingLogs={loadingLogs}
          />
          {logs.length ? (
            <Flex w="100%" justifyContent="center" paddingTop={4}>
              <PageNavigator
                canLoadMore={Boolean(cursor)}
                loadMore={loadMore}
              />
            </Flex>
          ) : null}
        </VStack>

        <LogExpandedView
          toast={toast}
          loadingLogs={loadingLogs}
          log={logs[logDisplayIndex]}
        />
      </HStack>
    </>
  )
}

function Header() {
  const { t } = useTranslation()
  return (
    <MainContentHeadingBox>
      <Stack direction={['column']} spacing={1}>
        <Heading as="h1" size="md" fontWeight="semibold" color="appDarkIndigo">
          {t('dam_audit_logs', 'DAM audit logs')}
        </Heading>
      </Stack>
    </MainContentHeadingBox>
  )
}

function FilterBar({
  users,
  startDate,
  endDate,
  path,
  requestId,
  disableApply,
  isRefreshing,
  setUserList,
  setOperations,
  setStartDate,
  setEndDate,
  setPath,
  setRequestId,
  setDisableApply,
  handleSubmit,
}) {
  const { t } = useTranslation()
  const DATE_BAD_ORDER_ERROR = useMemo(
    () => t('date_bad_error', 'Start date cannot be greater than the end date'),
    [],
  )

  const [dateError, setDateError] = useState<string | null>(null)

  const [customUsers, setCustomUsers] = useState<string[]>([])

  const allUsers = (users?.map(u => u.userEmail) || []).concat(customUsers)

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setDateError(DATE_BAD_ORDER_ERROR)
      setDisableApply(true)
      return
    }

    setDateError(null)
    setDisableApply(false)
  }, [startDate, endDate])

  return (
    <Box>
      <Flex align="center" gap="15px">
        {allUsers.length ? (
          <MultiSelectWithInput
            label={t('select_users', 'Select users')}
            allUsers={allUsers}
            customUsers={customUsers}
            onChange={selectedValues => setUserList(selectedValues)}
            setCustomUsers={setCustomUsers}
          />
        ) : (
          <></>
        )}

        <MultiSelectMenu
          label={t('select_operations', 'Select operations')}
          options={Object.values(MediaLibOperationEnum).map(option => ({
            label: <ChakraText>{String(option.label)}</ChakraText>,
            value: String(option.value),
          }))}
          onChange={selectedValues => {
            setOperations(selectedValues)
          }}
        />

        <DateSelector
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          handleDateSubmit={handleSubmit}
          disabled={disableApply || isRefreshing}
          isRefreshing={isRefreshing}
          dateError={dateError}
          width="180px"
        />
      </Flex>
      <Flex align="flex-start" gap="15px" marginTop="1rem">
        <SearchBar
          placeholder="Enter Path"
          searchQuery={path}
          setSearchQuery={setPath}
          minWidth="250px"
        />

        <SearchBar
          placeholder="Enter Request ID"
          searchQuery={requestId}
          setSearchQuery={setRequestId}
          minWidth="250px"
        />
      </Flex>
    </Box>
  )
}

function LogsTable({
  rowItems,
  getTableProps,
  headerGroups,
  getTableBodyProps,
  loadingLogs,
}) {
  return (
    <Flex maxH="65vh" overflow="scroll" minWidth="100%">
      <Table {...getTableProps}>
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column, i) => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps()
                  return (
                    <Th key={keyCol} {...restColumn} width={column.width}>
                      {column.render('Header')}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          <TableBody rowItems={rowItems} loadingLogs={loadingLogs} />
        </Tbody>
      </Table>
    </Flex>
  )
}

function TableBody({ rowItems, loadingLogs }) {
  const { t } = useTranslation()

  switch (true) {
    case loadingLogs && rowItems.length === 0: {
      return (
        <Tr>
          <Td colSpan={6} textAlign="center">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="appPaleGrey"
              color="appBlue"
              size="xl"
            />
          </Td>
        </Tr>
      )
    }
    case loadingLogs && rowItems.length > 0: {
      return (
        <>
          {rowItems}
          <Tr>
            <Td colSpan={6} textAlign="center">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Td>
          </Tr>
        </>
      )
    }
    case !loadingLogs && rowItems.length > 0: {
      return <>{rowItems}</>
    }
    default: {
      return (
        <Tr>
          <Td colSpan={6} textAlign="center">
            <ChakraText>
              {t('no_logs_available', 'No logs available')}
            </ChakraText>
          </Td>
        </Tr>
      )
    }
  }
}

function LogExpandedView({ loadingLogs, log, toast }) {
  const ASSET_TYPES = ['file', 'folder', 'media-collection'] as const

  const { t } = useTranslation()
  const [copyRequestId, setCopyRequestId] = useState<string>('')
  const [copyPath, setCopyPath] = useState<string>('')
  const [copyJobId, setCopyJobId] = useState<string>('')
  const { onCopy: onCopyForRequestID } = useClipboard(copyRequestId)
  const { onCopy: onCopyForPath } = useClipboard(copyPath)
  const { onCopy: onCopyForJobID } = useClipboard(copyJobId)

  const shouldDisableLink = (
    operation: MediaAuditLog['operation'],
  ): boolean => {
    return [
      'file.delete',
      'file.delete_by_move',
      'folder.delete',
      'folder.delete_by_move',
      'media-collection.delete',
    ].includes(operation)
  }

  const getAssetType = (
    log: MediaAuditLog,
  ): MediaAuditLog['entityType'] | null => {
    if (log.entityType) {
      return log.entityType
    }
    for (const assetType of ASSET_TYPES) {
      if (log.operation.startsWith(assetType)) {
        return assetType
      }
    }
    return null
  }

  const getDashboardLink = (log: MediaAuditLog): string => {
    const assetType = getAssetType(log)
    switch (assetType) {
      case 'file':
        return log.operation === 'file.delete_by_move'
          ? `/dashboard/media-library/detail/${log.details.info.movedTo.id}`
          : `/dashboard/media-library/detail/${log.entityId}`
      case 'folder':
        if (!log.path) {
          return '#'
        }
        return log.operation === 'folder.delete_by_move'
          ? `/dashboard/media-library/${Base64.encode(
              log.details.info.movedTo.path,
              true,
            )}`
          : `/dashboard/media-library/${Base64.encode(log.path, true)}`
      case 'media-collection':
        return `/dashboard/media-collections/${log.entityId}`
      default:
        return '#'
    }
  }

  useEffect(() => {
    if (copyRequestId) {
      onCopyForRequestID()
      toast({
        description: t(
          'copied_request_id_to_clipboard',
          'Copied Request ID to clipboard',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [copyRequestId])

  useEffect(() => {
    if (copyPath) {
      onCopyForPath()
      toast({
        description: t('copied_path_to_clipboard', 'Copied Path to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [copyPath])

  useEffect(() => {
    if (copyJobId) {
      onCopyForJobID()
      toast({
        description: t(
          'copied_job_id_to_clipboard',
          'Copied Job ID to clipboard',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [copyJobId])

  if (loadingLogs) {
    return (
      <Center width="full" height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  } else {
    return log ? (
      <VStack
        maxH="75vh"
        overflow="scroll"
        alignItems="flex-start"
        paddingLeft={4}
        paddingRight={4}
        w="100%"
      >
        <Heading
          as="h1"
          size="md"
          fontWeight="semibold"
          color="appDarkIndigo"
          paddingY={4}
        >
          {MediaLibOperationsValueToLabel[log.operation]}
        </Heading>
        <Grid
          width="100%"
          templateColumns="repeat(3, minmax(0px, 1fr))"
          columnGap={5}
          rowGap={5}
          paddingBottom={4}
        >
          <GridItem>
            <ChakraText fontWeight="medium">Method</ChakraText>
          </GridItem>
          <GridItem colSpan={2}>
            <ChakraText>{log.httpMethod}</ChakraText>
          </GridItem>
          <GridItem>
            <ChakraText fontWeight="medium">Endpoint</ChakraText>
          </GridItem>
          <GridItem colSpan={2}>
            <ChakraText>{log.endpoint}</ChakraText>
          </GridItem>
          <GridItem>
            <ChakraText fontWeight="medium">Request ID</ChakraText>
          </GridItem>
          <GridItem colSpan={2}>
            <Hover>
              {isHover => (
                <Flex>
                  <ChakraText>{log.requestId}</ChakraText>
                  {isHover ? (
                    <CopyIcon
                      cursor="pointer"
                      marginLeft="10px"
                      onClick={() => setCopyRequestId(log.requestId)}
                    />
                  ) : (
                    <Box minW="24px" />
                  )}
                </Flex>
              )}
            </Hover>
          </GridItem>
          <GridItem>
            <ChakraText fontWeight="medium">Time</ChakraText>
          </GridItem>
          <GridItem colSpan={2}>
            <Tooltip
              label={
                <>
                  <ChakraText>
                    {moment(log.timestamp)
                      .local()
                      .format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                    (local)
                  </ChakraText>
                  <ChakraText>
                    {moment
                      .utc(log.timestamp)
                      .format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                    (UTC)
                  </ChakraText>
                </>
              }
            >
              <ChakraText>
                {moment(log.timestamp)
                  .local()
                  .format('ddd, MMMM Do YYYY, HH:mm:ss')}
              </ChakraText>
            </Tooltip>
          </GridItem>
          <GridItem>
            <ChakraText fontWeight="medium">IP address</ChakraText>
          </GridItem>
          <GridItem colSpan={2}>
            <ChakraText>{log.ipAddress}</ChakraText>
          </GridItem>
          {log.name && (
            <>
              <GridItem>
                <ChakraText fontWeight="medium">Asset Name</ChakraText>
              </GridItem>
              <GridItem colSpan={2}>
                {getAssetType(log) === 'media-collection' &&
                !shouldDisableLink(log.operation) ? (
                  <Link
                    href={getDashboardLink(log)}
                    lineHeight="inherit"
                    fontWeight="normal"
                  >
                    {log.name}
                  </Link>
                ) : (
                  <ChakraText>{log.name}</ChakraText>
                )}
              </GridItem>
            </>
          )}
          {log.path && (
            <>
              <GridItem>
                <ChakraText fontWeight="medium">Asset Path</ChakraText>
              </GridItem>
              <GridItem colSpan={2}>
                <Hover>
                  {isHover => (
                    <Flex>
                      {shouldDisableLink(log.operation) ? (
                        <ChakraText>{log.path}</ChakraText>
                      ) : (
                        <Link
                          href={getDashboardLink(log)}
                          lineHeight="inherit"
                          fontWeight="normal"
                        >
                          {log.path}
                        </Link>
                      )}
                      {isHover ? (
                        <CopyIcon
                          cursor="pointer"
                          marginLeft="10px"
                          onClick={() => setCopyPath(log.path)}
                        />
                      ) : (
                        <Box minW="24px" />
                      )}
                    </Flex>
                  )}
                </Hover>
              </GridItem>
            </>
          )}
          {['file.delete_by_move', 'folder.delete_by_move'].includes(
            log.operation,
          ) && (
            <>
              <GridItem>
                <ChakraText fontWeight="medium">Moved To</ChakraText>
              </GridItem>
              <GridItem colSpan={2}>
                <Hover>
                  {isHover => (
                    <Flex>
                      <Link
                        href={getDashboardLink(log)}
                        lineHeight="inherit"
                        fontWeight="normal"
                      >
                        {log.details.info.movedTo.path}
                      </Link>
                      {isHover ? (
                        <CopyIcon
                          cursor="pointer"
                          marginLeft="10px"
                          onClick={() =>
                            setCopyPath(log.details.info.movedTo.path)
                          }
                        />
                      ) : (
                        <Box minW="24px" />
                      )}
                    </Flex>
                  )}
                </Hover>
              </GridItem>
            </>
          )}
          {log.details?.info?.jobId && (
            <>
              <GridItem>
                <ChakraText fontWeight="medium">Job ID</ChakraText>
              </GridItem>
              <GridItem colSpan={2}>
                <Hover>
                  {isHover => (
                    <Flex>
                      <ChakraText>{log.details.info.jobId}</ChakraText>
                      {isHover ? (
                        <CopyIcon
                          cursor="pointer"
                          marginLeft="10px"
                          onClick={() => setCopyJobId(log.details.info.jobId)}
                        />
                      ) : (
                        <Box minW="24px" />
                      )}
                    </Flex>
                  )}
                </Hover>
              </GridItem>
            </>
          )}
        </Grid>
        {log.requestBody && (
          <>
            <Divider />
            <Heading
              as="h1"
              size="md"
              fontWeight="semibold"
              color="appDarkIndigo"
              paddingY={4}
            >
              Request Body
            </Heading>
            <Box
              bg="rgb(25, 48, 85)"
              color="#878b8e"
              paddingBottom="10px"
              width="100%"
            >
              <ReactSyntaxHighlighterWrapper
                language="json"
                style={vs2015}
                wrapLines={true}
                customStyle={codeBlockStyles}
                showLineNumbers={false}
                codeTagProps={{
                  style: {
                    fontFamily: 'monospace,monospace !important',
                  },
                }}
              >
                {JSON.stringify(log.requestBody, null, 4)}
              </ReactSyntaxHighlighterWrapper>
            </Box>
          </>
        )}
      </VStack>
    ) : (
      <Center width="full" height="full">
        <ChakraText>{t('nothing_to_display', 'Nothing to display')}</ChakraText>
      </Center>
    )
  }
}

function PageNavigator({
  loadMore,
  canLoadMore,
}: {
  loadMore(): void
  canLoadMore: boolean
}): React.ReactElement {
  const { t } = useTranslation()
  return (
    <Stack direction={['row']} align="center" spacing={2}>
      <Box
        cursor="pointer"
        padding="7px"
        color={canLoadMore ? colors.appBlue : colors.appBlueyGrey}
        onClick={loadMore}
      >
        <ChakraText>{t('load_more', 'Load More')}</ChakraText>
      </Box>
    </Stack>
  )
}

function getColumns({ t, users }) {
  return [
    {
      Header: t('operation', 'Operation').toString(),
      accessor: value => MediaLibOperationsValueToLabel[value.operation],
      Cell: ({ value }: CellProps<MediaAuditLog, string>) => (
        <Tooltip label={value}>
          <ChakraText maxW="150px" isTruncated>
            {value}
          </ChakraText>
        </Tooltip>
      ),
    },
    {
      Header: t('user', 'User').toString(),
      accessor: value => {
        if (value.userId) {
          // eslint-disable-next-line no-underscore-dangle
          const exists = users?.some(obj => obj._id === value.userId)
          return {
            isDeleted: !exists,
            userName: value.userName,
            userEmail: value.userEmail,
          }
        } else {
          return {
            apiKey: value.publicApiKey,
          }
        }
      },
      width: '120px',
      Cell: ({
        value,
      }: CellProps<
        MediaAuditLog,
        {
          isDeleted?: boolean
          userName?: string
          userEmail?: string
          apiKey?: string
        }
      >) => {
        if (value?.apiKey) {
          return (
            <Tooltip
              label={<ChakraText>{`API Key: ${value.apiKey}`}</ChakraText>}
            >
              <Flex
                maxW="150px"
                alignItems="center"
                justifyContent="space-between"
              >
                <ChakraText maxW="100%" isTruncated>
                  {`API Key: ${value.apiKey}`}
                </ChakraText>
              </Flex>
            </Tooltip>
          )
        } else {
          return (
            <Tooltip
              label={
                <>
                  <ChakraText>
                    {`Name: ${value.userName} ${
                      value.isDeleted ? '(deleted)' : ''
                    }`}
                  </ChakraText>
                  <ChakraText>{`Email: ${value.userEmail}`}</ChakraText>
                </>
              }
            >
              <Flex
                maxW="150px"
                alignItems="center"
                justifyContent="space-between"
              >
                <ChakraText maxW="100%" isTruncated>
                  {`${value.userName}`}
                </ChakraText>
                <ChakraText
                  ml={1}
                  fontStyle="italic"
                  color="appBattleshipGrey"
                  fontSize="xs"
                >
                  {value.isDeleted ? '(deleted)' : ''}
                </ChakraText>
              </Flex>
            </Tooltip>
          )
        }
      },
    },
    {
      Header: t('timestamp', 'Time').toString(),
      accessor: value => value.timestamp,
      Cell: ({ value }: CellProps<MediaAuditLog, string>) => (
        <Tooltip
          label={
            <>
              <ChakraText>
                {moment(value).local().format('ddd, MMMM Do YYYY, HH:mm:ss')}{' '}
                (local)
              </ChakraText>
              <ChakraText>
                {moment.utc(value).format('ddd, MMMM Do YYYY, HH:mm:ss')} (UTC)
              </ChakraText>
            </>
          }
        >
          <ChakraText>
            {moment(value).local().format('ddd, MMMM Do YYYY, HH:mm:ss')}
          </ChakraText>
        </Tooltip>
      ),
    },
  ]
}

function MultiSelectWithInput({
  label,
  allUsers,
  customUsers,
  onChange,
  setCustomUsers,
}: {
  label: string
  allUsers: string[]
  customUsers: string[]
  onChange(selectedValues: string[]): void
  setCustomUsers: React.Dispatch<React.SetStateAction<string[]>>
}): JSX.Element {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [inputValue, setInputValue] = useState<string>('')

  const removeCustomUser = user => {
    setCustomUsers(customUsers => customUsers.filter(u => u !== user))
  }

  const handleKeyPress = event => {
    if (event.key === 'Enter' && inputValue) {
      setCustomUsers(customUsers => customUsers.concat([inputValue]))
      setSelectedOptions(selectedOptions =>
        selectedOptions.concat([inputValue]),
      )
      setInputValue('')
    }
  }

  const handleChange = values => {
    setSelectedOptions(values)
  }

  useEffect(() => {
    onChange(selectedOptions)
  }, [selectedOptions])

  useEffect(() => {
    if (selectedOptions.length) {
      setSelectedOptions(values => values.filter(v => allUsers.includes(v)))
    }
  }, [customUsers])

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        color={selectedOptions.length ? 'appBlue' : '#ced1d6'}
        border="solid 0.5px"
        borderRadius="5px"
        borderColor={selectedOptions.length ? 'appBlue' : '#ced1d6'}
        minW="250px"
        minH="35px"
        textAlign="start"
        paddingLeft={4}
      >
        {`${label}${
          selectedOptions.length > 0 ? ` (${selectedOptions.length})` : ''
        }`}
        <ChevronDownIcon />
      </MenuButton>
      <MenuList minW="250px" maxH="350px" overflow="scroll">
        <MenuGroup>
          <MenuItem onClick={() => setSelectedOptions([])}>Clear all</MenuItem>
        </MenuGroup>
        <MenuDivider />
        <MenuOptionGroup
          value={selectedOptions}
          type="checkbox"
          onChange={handleChange}
        >
          {allUsers.map(userEmail => (
            <MenuItemOption
              key={`multiselect-menu-${userEmail}`}
              value={userEmail}
            >
              <Flex
                maxW="250px"
                alignItems="center"
                justifyContent="space-between"
              >
                <ChakraText isTruncated>{`${userEmail}`}</ChakraText>
                {customUsers.includes(userEmail) && (
                  <IconButton
                    aria-label="Remove email"
                    size="xs"
                    colorScheme="gray"
                    variant="ghost"
                    icon={<CloseIcon />}
                    onClick={() => removeCustomUser(userEmail)}
                  />
                )}
              </Flex>
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
        <MenuDivider />
        <MenuGroup>
          <Input
            variant="flushed"
            placeholder="Enter custom email"
            marginLeft="1rem"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </MenuGroup>
      </MenuList>
    </Menu>
  )
}
